// React
import React from "react";

// MUI
import { Button, Stack, Typography } from "@mui/material";

// Third Party
import { Moment } from "moment";

// Helpers
import { and } from "@Common/helpers/sonar";
import { getEndOfDay, getStartOfDay } from "../helpers";

// Components
import DateTimePickerControlledV2 from "@Layouts/components/DateTimePicker/DateTimePickerControlledV2";
import DatePickerRangeSkeleton from "../skeletons/DatePickerRangeSkeleton";

// Types
import { IReviewDateRange } from "@DataPages/types/socialMediaReviews/utils";

type ReviewsDatePickerProps = Readonly<{
	isLoading?: boolean;
	isDisabled?: boolean;
	containerStyle?: React.CSSProperties;
	minDate: string;
	maxDate: string;
	selectedDates: IReviewDateRange | null;
	isDataPresent?: boolean;
	// Callbacks
	setSelectedDates: React.Dispatch<React.SetStateAction<IReviewDateRange | null>>;
}>;

type ReviewDateRange = {
	startDate?: Moment;
	endDate?: Moment;
};

export default function ReviewsDatePicker({
	isLoading,
	containerStyle,
	minDate,
	maxDate,
	isDisabled = false,
	selectedDates,
	isDataPresent = true,
	// Callbacks
	setSelectedDates,
}: ReviewsDatePickerProps) {
	/********************************************* HANDLERS ************************************************/
	const handleOnUpdate = (dataTime: Moment, type: keyof ReviewDateRange) => {
		setSelectedDates((prev) => {
			return {
				...(prev ?? {}),
				[type]: dataTime,
			};
		});
	};

	const handleReset = () => {
		setSelectedDates(null);
	};

	if (isLoading) {
		return <DatePickerRangeSkeleton />;
	}

	if (!isDataPresent) {
		return null;
	}

	return (
		<Stack direction={"row"} alignItems={"center"} spacing={1} width={"fit-content"} mt={2} sx={containerStyle}>
			<DateTimePickerControlledV2
				value={selectedDates?.startDate as Date}
				disabled={isDisabled}
				minDate={getStartOfDay(minDate)}
				maxDate={selectedDates?.endDate ? getEndOfDay(selectedDates?.endDate?.toString()) : getEndOfDay(maxDate)}
				views={["year", "month", "day"]}
				viewRenderers={{
					hours: null,
					minutes: null,
					seconds: null,
				}}
				width={"130px"}
				hideTabs
				inputProps={{
					placeholder: "mm/dd/yyyy",
				}}
				// Callbacks
				onActionUpdate={(dataTime) => handleOnUpdate(dataTime, "startDate")}
			/>

			<Typography component={"span"} className={"!font-sans text-[14.5px] text-default"}>
				to
			</Typography>

			<DateTimePickerControlledV2
				value={selectedDates?.endDate as Date}
				disabled={isDisabled || !selectedDates?.startDate}
				minDate={getStartOfDay(selectedDates?.startDate?.toString() ?? "")}
				maxDate={getEndOfDay(maxDate)}
				views={["year", "month", "day"]}
				viewRenderers={{
					hours: null,
					minutes: null,
					seconds: null,
				}}
				width={"130px"}
				hideTabs
				inputProps={{
					placeholder: "mm/dd/yyyy",
				}}
				// Callbacks
				onActionUpdate={(dataTime) => handleOnUpdate(dataTime, "endDate")}
			/>

			<Button
				variant={"text"}
				className={"!font-sans text-[14.5px] !text-primary font-normal !underline !normal-case !ml-3"}
				disabled={and(!selectedDates?.startDate, !selectedDates?.endDate)}
				onClick={handleReset}
				classes={{ disabled: "!text-disabled" }}
				sx={{
					padding: 0,
					minWidth: 0,
					"&:hover": {
						backgroundColor: "transparent",
					},
				}}
			>
				<span
					style={{
						cursor: "pointer",
						display: "inline-block",
					}}
				>
					Reset
				</span>
			</Button>
		</Stack>
	);
}
